import type {
  IconProps
} from '@chakra-ui/react'
import {
  Box,
  Button,
  HStack,
  Icon,
  Skeleton,
  Stack,
  Text
} from '@chakra-ui/react'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faCalendarArrowUp, faCheck, faExclamationTriangle, faShieldKeyhole, faXmark } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { CustomLinkOverlay } from 'BrokerTsLib/src/components'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { UserLink } from '@/components'
import { TableSectionRow } from '@/components/Section/TableSectionRow'
import { useAuth } from '@/context/AuthContext'
import type { PpzAuthorizationGridItem } from '@/dtos'
import { useApprove, useGetByUser } from '@/hooks/usePpzAuthorization'
import { Add, Check, Refresh } from '@/icons'
import { formatDate } from '@/utils/formatDate'

export const PpzAuthorizationSection = ({ userId }: { userId: string }) => {
  const { data: ppzAuthorizations, isLoading, refetch, error } = useGetByUser(userId)

  return (
    <TableSectionRow
      icon={<Icon as={FontAwesomeIcon} icon={faShieldKeyhole} size="xl" />}
      title="Autorizace PPZ"
    >
      <Skeleton isLoaded={!isLoading} minH={8} mt={1} mb={3}>
        {error ? (
          <HStack>
            <Button size='sm' variant='ghost' colorScheme='red' onClick={() => refetch()}>
              <Refresh />
            </Button>
            <Text>Při získávání dat došlo k chybě.</Text>
          </HStack>
        ) : <Stack>
          {ppzAuthorizations?.length === 0 ?
            <HStack>
              <Icon as={FontAwesomeIcon} icon={faExclamationTriangle} size="lg" color='orange.600' />
              <Text>Dosud nebyla vložena autorizace PPZ.</Text>
            </HStack>
            :
            ppzAuthorizations?.map((ppzAuthorization, index) => (
              <CustomLinkOverlay key={index} to={`/ppzAuthorization/${ppzAuthorization.id}`}>
                <PpzAuthorizationListItem key={index} {...ppzAuthorization} />
              </CustomLinkOverlay>
            ))}
        </Stack>}
      </Skeleton>
      <Button
        colorScheme="primary"
        size="md"
        variant="link"
        as={Link}
        to={`/ppzAuthorization/create?userId=${userId}`}
        justifySelf='left'
        mt={1}
        ml={1.5}
        mb={1}
        display='flex'
        gap={2}
      >
        <Add />
        <Box>Přidat autorizaci</Box>
      </Button>

    </TableSectionRow>
  )
}

export const PpzAuthorizationListItem = (ppzAuthorization: PpzAuthorizationGridItem) => {
  const { authorizationValidTo, authorizationValidFrom, approvedDate, approvedById, approvedBy } = ppzAuthorization

  const actual = authorizationValidFrom <= new Date() && authorizationValidTo >= new Date() && approvedBy

  const notApproved = authorizationValidFrom <= new Date() && authorizationValidTo >= new Date() && !approvedBy

  const expired = authorizationValidTo <= new Date()

  const upcoming = authorizationValidFrom > new Date()

  if (actual)
    return <PpzAuthorizationListItemWrapper icon={faCheck} iconColor='green.600' >
      Platná do {formatDate(authorizationValidTo)}. Autorizaci
      potvrdil{' '}
      {approvedDate &&
        'dne ' + formatDate(approvedDate)}{' '}
      <UserLink
        userId={approvedById}
        name={approvedBy}
      />
      .
    </PpzAuthorizationListItemWrapper>

  if (notApproved)
    return NotApprovedPpzAuthorizationListItem(ppzAuthorization)

  if (expired)
    return <PpzAuthorizationListItemWrapper icon={faXmark} iconColor='red.600' >
      Platnost autorizace vypršela dne{' '}
      {formatDate(authorizationValidTo)}. Autorizaci potvrdil{' '}
      {approvedDate &&
        'dne ' + formatDate(approvedDate)}{' '}
      <UserLink
        userId={approvedById}
        name={approvedBy}
      />
      .
    </PpzAuthorizationListItemWrapper>

  if (upcoming)
    return <PpzAuthorizationListItemWrapper icon={faCalendarArrowUp} iconColor='green.600' >
      Autorizace bude platná od{' '}
      {formatDate(authorizationValidFrom)}.
    </PpzAuthorizationListItemWrapper>
}


const PpzAuthorizationListItemWrapper = ({ children, icon, iconColor }: { children: ReactNode, icon: IconProp, iconColor: IconProps['color'] }) =>
  <HStack background='neutral.50' p={2} borderRadius='sm'>
    <Icon as={FontAwesomeIcon} icon={icon} size="lg" px={1} color={iconColor} />
    <Box w='full'>
      {children}
    </Box>
  </HStack>

function NotApprovedPpzAuthorizationListItem({ authorizationValidTo, id }: PpzAuthorizationGridItem) {
  const { user } = useAuth()

  const { mutateAsync, isLoading } = useApprove(id)

  return <PpzAuthorizationListItemWrapper icon={faExclamationTriangle} iconColor='orange.600'>
    <HStack w='full' justifyContent='space-between'>
      <Text>
        Platná do {formatDate(authorizationValidTo)}. Autorizace není potvrzena.
      </Text>
      {user?.isSupervisor &&
        <Button colorScheme='green' size='sm' m={1} onClick={() => mutateAsync()} isLoading={isLoading}><Check boxSize={3.5} /> Potvrdit</Button>}
    </HStack>
  </PpzAuthorizationListItemWrapper>
}

